import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import styled from 'styled-components'
import {
  map,
  prop,
  values as rValues,
  pipe,
  flatten,
  values,
  length,
  complement,
  isEmpty,
  isNil,
  pathOr,
  ifElse,
  find,
  propEq,
  path
} from 'ramda'
import { select } from '@/store'
import { breakpoints, colors } from '@/styles'
import { createModal, useModalize } from '@/components/modalize/mui'
import { Button } from '@/components/button'
import { Label } from '@/components/text'
import ModalBase from '@/components/modal/modal-base'
import Context from '@/components/modal/context'
import useMediaQuery from '@/hooks/use-media-query'
import RecipientComponent from './recipient-component'
import { show as showSelectRecipientModal } from '../add-signer/add-recipient-modal'
import { show as showSelectRecipientFormModal } from '../add-signer/add-recipient-form-modal'
import AddNewSignerIcon from '../add-signer/add-new-signer-icon.svg'

const MAX_SIGNEES = 7

const Wrapper = styled.div`
  margin: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  flex: 1;
  width: 100%;
`

const ButtonsWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: ${props => (props.editing ? 'space-between' : 'flex-end')};

  button:first-child {
    margin-right: 16px;
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    button {
      width: 50%;
      margin: 0;
    }
  }
`
const AddNewSignerContainer = styled.button`
  background: none;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 16px 0px 24px;
  width: 100%;
  height: 56px;
  border: 1.5px dashed ${colors.blue88};
  border-radius: 8px;
  cursor: pointer;

  &:hover,
  &:active {
    background-color: ${colors.blue96};
    border: 1.5px dashed ${colors.blue80};
  }
`
const AddNewSignerIconStyle = styled.img`
  width: 32px;
  height: 32px;
  margin-right: 8px;
`

const AddNewSignerContainerText = styled(Label)`
  color: ${colors.blue16};
`
const getApplicants = pipe(rValues, map(prop('applicants')), flatten, complement(isEmpty))
const isNotNil = complement(isNil)

const ManageRecipientsModal = ({
  contractId,
  ModalTitle,
  ModalFooter,
  editing = false,
  signees,
  setSignees,
  fileMetadata,
  setFileMetadata,
  currentUser,
  setUser,
  items,
  setItems,
  currentDocument
}) => {
  const { t } = useTranslation()
  const { close } = useModalize()
  const dispatch = useDispatch()
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)

  const {
    recipients = {},
    applicants,
    packageFiles
  } = useSelector(state => select.contract.contractPackageInfo(state, { contractId }))

  const document = pipe(
    pathOr([], [0, 'contract_documents']),
    ifElse(
      () => isNotNil(currentDocument),
      find(propEq('listing_contract_package_file_id', Number(currentDocument))),
      path([0])
    )
  )(recipients)

  useEffect(() => {
    async function fetchData() {
      await dispatch.contract.getRecipients({ contractId })
    }

    if (isEmpty(recipients) || (contractId && !document)) {
      fetchData()
    }
  }, [dispatch.contract, contractId])

  useEffect(() => {
    async function fetchData() {
      await dispatch.contract.getContractApplicants({ contractId: contractId })
    }

    if (isNil(applicants)) fetchData()
  }, [])

  const hasApplicants = getApplicants(applicants)

  const handleAddRecipient = () =>
    !hasApplicants
      ? showSelectRecipientFormModal({
          contractId,
          signees,
          setSignees,
          fileMetadata,
          setFileMetadata,
          currentUser,
          setUser,
          items
        })
      : showSelectRecipientModal({
          contractId,
          signees,
          setSignees,
          fileMetadata,
          setFileMetadata,
          currentUser,
          setUser
        })

  const formattedRecipients = values(recipients)

  return (
    <Context.Provider
      value={{
        ModalTitle,
        ModalFooter,
        close
      }}
    >
      <ModalBase
        overflow="visible"
        minHeight={295}
        title={t('l.contract.create.recipients.title', 'Recipients')}
      >
        <Wrapper>
          {formattedRecipients.map(recipient => (
            <RecipientComponent
              key={recipient.recipient_id}
              recipient={recipient}
              contractId={contractId}
              items={items}
              setItems={setItems}
              signees={signees}
              setSignees={setSignees}
              fileMetadata={fileMetadata}
              setFileMetadata={setFileMetadata}
              currentUser={currentUser}
              setUser={setUser}
              currentDocument={currentDocument}
              packageFiles={packageFiles}
              recipients={recipients}
            />
          ))}
          {length(formattedRecipients) < MAX_SIGNEES && (
            <AddNewSignerContainer onClick={handleAddRecipient} type="button">
              <AddNewSignerIconStyle src={AddNewSignerIcon} alt="" />
              <AddNewSignerContainerText fontSize="14px">
                {t('l.contract_edit.add_recipient.recipients.button', 'Add Recipient')}
              </AddNewSignerContainerText>
            </AddNewSignerContainer>
          )}
          <ModalFooter>
            <ButtonsWrapper editing={editing}>
              <Button
                type="button"
                theme="primary"
                onClick={close}
                size={displayMobile ? 'medium' : 'small'}
              >
                {t('common.done', 'Done')}
              </Button>
            </ButtonsWrapper>
          </ModalFooter>
        </Wrapper>
      </ModalBase>
    </Context.Provider>
  )
}

export const show = createModal(ManageRecipientsModal, {
  width: '640px',
  sheet: true,
  overflow: 'visible'
})
